import React, { useState, useCallback } from 'react';
import { Bell, X, Info, CheckCircle, AlertTriangle, AlertCircle, Crown } from 'lucide-react';
import { useNotificationStore, NotificationType } from '../store/notificationStore';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const NotificationCenter: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, dismissNotification, markAsRead, clearAll } = useNotificationStore();
  const { t } = useTranslation();
  const unreadCount = notifications.filter(n => !n.read).length;

  const getIcon = (type: NotificationType, isAdmin: boolean = false) => {
    if (isAdmin) {
      return <Crown className="w-5 h-5 text-purple-500 dark:text-purple-400" />;
    }
    switch (type) {
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
    }
  };

  const getBackgroundColor = (type: NotificationType, isAdmin: boolean = false) => {
    if (isAdmin) {
      return 'bg-purple-50 dark:bg-purple-900/50 border border-purple-100 dark:border-purple-800';
    }
    switch (type) {
      case 'info':
        return 'bg-blue-50 dark:bg-blue-900/50 border border-blue-100 dark:border-blue-800';
      case 'success':
        return 'bg-green-50 dark:bg-green-900/50 border border-green-100 dark:border-green-800';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/50 border border-yellow-100 dark:border-yellow-800';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/50 border border-red-100 dark:border-red-800';
    }
  };

  const getTextColor = (type: NotificationType, isAdmin: boolean = false) => {
    if (isAdmin) {
      return 'text-purple-800 dark:text-purple-200';
    }
    switch (type) {
      case 'info':
        return 'text-blue-800 dark:text-blue-200';
      case 'success':
        return 'text-green-800 dark:text-green-200';
      case 'warning':
        return 'text-yellow-800 dark:text-yellow-200';
      case 'error':
        return 'text-red-800 dark:text-red-200';
    }
  };

  const handleNotificationClick = (id: string) => {
    markAsRead(id);
  };

  const handleActionClick = (e: React.MouseEvent, notification: any) => {
    e.stopPropagation();
    if (notification.action) {
      notification.action.onClick();
      dismissNotification(notification.id);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
      >
        <Bell className="w-6 h-6" />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute top-0 right-0 -mt-1 -mr-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-600 dark:bg-red-500 rounded-full"
            >
              {unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-30"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              className="fixed right-0 sm:absolute sm:right-0 top-16 sm:top-auto sm:mt-2 w-full sm:w-[380px] border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-xl sm:rounded-xl shadow-lg z-40 overflow-hidden max-h-[80vh] sm:max-h-[600px]"
            >
              <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('notifications.title')}
                </h3>
                {notifications.length > 0 && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={clearAll}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    {t('notifications.clearAll')}
                  </motion.button>
                )}
              </div>

              <div className="overflow-y-auto max-h-[calc(80vh-4rem)] sm:max-h-[500px]">
                {notifications.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 text-center text-gray-500 dark:text-gray-400"
                  >
                    {t('notifications.empty')}
                  </motion.div>
                ) : (
                  <AnimatePresence mode="popLayout">
                    {notifications.map((notification) => (
                      <motion.div
                        key={notification.id}
                        layout
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        className={`p-4 ${
                          !notification.read ? 'bg-gray-50 dark:bg-gray-700/50' : ''
                        } ${getBackgroundColor(notification.type, notification.isAdmin)}`}
                        onClick={() => handleNotificationClick(notification.id)}
                      >
                        <div className="flex items-start">
                          <motion.div
                            initial={{ rotate: -180, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            className="flex-shrink-0"
                          >
                            {getIcon(notification.type, notification.isAdmin)}
                          </motion.div>
                          <div className="ml-3 flex-1">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-medium ${getTextColor(notification.type, notification.isAdmin)}`}>
                                {notification.title}
                              </p>
                              {notification.isAdmin && (
                                <span className="px-2 py-0.5 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900 rounded-full">
                                  Xavier
                                </span>
                              )}
                            </div>
                            <p className={`mt-1 text-sm ${getTextColor(notification.type, notification.isAdmin)}`}>
                              {notification.message}
                            </p>
                            {notification.imageUrl && (
                              <div className="mt-2 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                <img
                                  src={notification.imageUrl}
                                  alt=""
                                  className="w-full h-32 object-cover"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                            {notification.action && (
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={(e) => handleActionClick(e, notification)}
                                className="mt-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500"
                              >
                                {notification.action.label}
                              </motion.button>
                            )}
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissNotification(notification.id);
                            }}
                            className="ml-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <X className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCenter;